import useLatestRequest from '../hooks/useLatestRequest';
import RefreshRegion from '../components/common/RefreshRegion';
import ListPagination from '../components/common/ListPagination';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LayoutGroup, MotionConfig, motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import EmployeeAvatar from '../components/common/EmployeeAvatar';
import { useHeaderSearch } from '../contexts/SearchContext';
import LeaveService from '../services/leave.service';
import EmployeeService from '../services/employee.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const PAGE_SIZE = 20;

const LeaveApproval = () => {
  const { headerSearch } = useHeaderSearch();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0, all: 0 });
  const { loading, begin: beginFetchLeaveRequests } = useLatestRequest();
  const [filter, setFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRequests, setTotalRequests] = useState(0);
  const [displayedPage, setDisplayedPage] = useState('pending-1');
  const pageTopRef = useRef(null);
  const scrollToPageRef = useRef(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decisionAction, setDecisionAction] = useState('approved');
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [employeeDirectory, setEmployeeDirectory] = useState({});
  const [balanceEmployeeEmail, setBalanceEmployeeEmail] = useState('');
  const [balanceDetails, setBalanceDetails] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({ adjustmentType: 'CREDIT', days: '0.5', reason: '' });
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [cancellationPage, setCancellationPage] = useState(1);
  const [cancellationTotalPages, setCancellationTotalPages] = useState(0);
  const [cancellationTotal, setCancellationTotal] = useState(0);
  const { loading: cancellationsLoading, begin: beginCancellations } = useLatestRequest();

  useEffect(() => {
    fetchEmployeeDirectory();
  }, []);

  const fetchCancellationRequests = useCallback(async () => {
    const request = beginCancellations();
    try {
      const response = await LeaveService.getCanceledRequestsByManager(cancellationPage);
      if (!request.isCurrent()) return;
      if (!response?.requests?.length && cancellationPage > 1) {
        setCancellationPage(cancellationPage - 1);
        return;
      }
      setCancellationRequests(response?.requests || []);
      setCancellationTotalPages(response?.totalPages || 0);
      setCancellationTotal(response?.totalRequests ?? response?.requests?.length ?? 0);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error loading leave cancellation requests:', error);
      setCancellationRequests([]);
      setCancellationTotalPages(0);
      setCancellationTotal(0);
    } finally {
      request.finish();
    }
  }, [cancellationPage, beginCancellations]);

  useEffect(() => {
    fetchCancellationRequests();
  }, [fetchCancellationRequests]);

  const fetchLeaveRequests = useCallback(async () => {
    const request = beginFetchLeaveRequests();
    try {
      const response = await LeaveService.getLeaveRequests({ status: filter, page: currentPage });
      if (!request.isCurrent()) return;
      const requests = response?.requests || response || [];
      // A decision or cancellation can remove the last request on this page.
      if (Array.isArray(requests) && requests.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        return;
      }
      setLeaveRequests(requests);
      setDisplayedPage(`${filter}-${currentPage}`);
      setTotalPages(response?.totalPages || 0);
      setTotalRequests(response?.totalRequests ?? response?.statusCounts?.[filter] ?? 0);
      setStatusCounts(response?.statusCounts || { pending: 0, approved: 0, rejected: 0, cancelled: 0, all: 0 });
    } catch (error) {
      if (!request.isCurrent()) return;
      scrollToPageRef.current = null;
      console.error('Error fetching leave requests:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to load leave requests';
      toast.error(errorMessage);
      setLeaveRequests([]);
      setTotalPages(0);
      setTotalRequests(0);
      setStatusCounts({ pending: 0, approved: 0, rejected: 0, cancelled: 0, all: 0 });
    } finally {
      request.finish();
    }
  }, [filter, currentPage, beginFetchLeaveRequests]);

  useEffect(() => {
    fetchLeaveRequests();
  }, [fetchLeaveRequests]);

  useEffect(() => {
    if (!loading && scrollToPageRef.current === displayedPage) {
      scrollToPageRef.current = null;
      pageTopRef.current?.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  }, [loading, displayedPage]);

  const fetchEmployeeDirectory = async () => {
    try {
      const response = await EmployeeService.getAll();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      const byEmail = list.reduce((acc, emp) => {
        const key = String(emp.email_address || '').toLowerCase();
        if (key) acc[key] = emp;
        return acc;
      }, {});
      setEmployeeDirectory(byEmail);
    } catch (error) {
      console.error('Error loading employee directory for avatars:', error);
      setEmployeeDirectory({});
    }
  };

  const handleApproval = async (requestId, status, reason = '', shouldSendEmail = true) => {
    try {
      setSubmittingDecision(true);
      const payload = {
        status: status.toUpperCase(),
        sendEmail: shouldSendEmail,
      };
      if (reason) {
        payload.reason = reason;
      }
      const response = await LeaveService.updateLeaveRequest(requestId, payload);
      if (shouldSendEmail && response?.emailSent === false) {
        toast.warning(`Leave request ${status} successfully, but the email could not be sent.`);
      } else if (shouldSendEmail) {
        toast.success(`Leave request ${status} and email sent successfully${response?.attendanceUpdated ? `. ${response.attendanceUpdated} attendance record(s) updated.` : ''}`);
      } else {
        toast.success(`Leave request ${status} successfully${response?.attendanceUpdated ? `. ${response.attendanceUpdated} attendance record(s) updated.` : ''}`);
      }
      await fetchLeaveRequests();
      closeDecisionModal();
    } catch (error) {
      console.error('Error updating leave request:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to update leave request';
      toast.error(errorMessage);
    } finally {
      setSubmittingDecision(false);
    }
  };

  const decideCancellation = async (request, status) => {
    try {
      await LeaveService.updateCancelRequest(request.id, { status, leaveId: request.leaveRequestId });
      toast.success(`Leave cancellation ${status.toLowerCase()} successfully.`);
      await Promise.all([fetchCancellationRequests(), fetchLeaveRequests()]);
    } catch (error) {
      toast.error(error.message || 'Failed to update cancellation request');
    }
  };

  const fetchBalanceDetails = async (employeeEmail) => {
    if (!employeeEmail) {
      setBalanceDetails(null);
      return;
    }

    try {
      setBalanceLoading(true);
      const response = await LeaveService.getEmployeeLeaveBalanceDetails(employeeEmail);
      setBalanceDetails(response);
    } catch (error) {
      console.error('Error loading employee leave balance:', error);
      toast.error(error.message || 'Failed to load employee leave balance');
      setBalanceDetails(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleBalanceEmployeeChange = (event) => {
    const employeeEmail = event.target.value;
    setBalanceEmployeeEmail(employeeEmail);
    fetchBalanceDetails(employeeEmail);
  };

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setAdjustmentForm({ adjustmentType: 'CREDIT', days: '0.5', reason: '' });
  };

  const submitAdjustment = async () => {
    const days = Number(adjustmentForm.days);
    if (!balanceEmployeeEmail || !adjustmentForm.reason.trim() || !Number.isFinite(days) || days <= 0) {
      toast.error('Enter adjustment days and a reason.');
      return;
    }

    try {
      setSubmittingAdjustment(true);
      const response = await LeaveService.createLeaveBalanceAdjustment({
        employeeEmail: balanceEmployeeEmail,
        adjustmentType: adjustmentForm.adjustmentType,
        days,
        reason: adjustmentForm.reason.trim(),
      });
      setBalanceDetails(response);
      toast.success(`Paid leave balance ${adjustmentForm.adjustmentType === 'CREDIT' ? 'increased' : 'decreased'} by ${days} day(s).`);
      closeAdjustmentModal();
    } catch (error) {
      console.error('Error adjusting leave balance:', error);
      toast.error(error.message || 'Failed to adjust leave balance');
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  const closeDecisionModal = () => {
    setShowDecisionModal(false);
    setDecisionReason('');
    setSendEmail(true);
    setSelectedRequest(null);
  };

  const openDecisionModal = (request, action) => {
    setSelectedRequest(request);
    setDecisionAction(action);
    setDecisionReason('');
    setSendEmail(true);
    setShowDecisionModal(true);
  };

  const confirmDecision = () => {
    if (selectedRequest) {
      handleApproval(selectedRequest.id, decisionAction, decisionReason.trim(), sendEmail);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const safeLeaveRequests = (Array.isArray(leaveRequests) ? leaveRequests : []).filter(req => {
    if (!headerSearch) return true;
    const q = headerSearch.toLowerCase();
    return (
      (req.employeeName && req.employeeName.toLowerCase().includes(q)) ||
      (req.email && req.email.toLowerCase().includes(q)) ||
      (req.leaveType && req.leaveType.toLowerCase().includes(q)) ||
      (req.reason && req.reason.toLowerCase().includes(q)) ||
      (req.status && req.status.toLowerCase().includes(q))
    );
  });
  
  const filterOptions = [
    { value: 'pending', label: 'Pending', count: statusCounts.pending },
    { value: 'approved', label: 'Approved', count: statusCounts.approved },
    { value: 'rejected', label: 'Rejected', count: statusCounts.rejected },
    { value: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
    { value: 'all', label: 'All', count: statusCounts.all }
  ];
  const employeeOptions = Object.values(employeeDirectory).sort((a, b) =>
    `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`)
  );


  return (
    <MotionConfig reducedMotion="user">
    <div ref={pageTopRef} className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Leave Approval</h1>
            <p className="text-blue-100">Review and approve employee leave requests</p>
          </div>
          <div className="w-full md:w-80">
            <label htmlFor="leave-balance-employee" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-blue-100">
              Employee leave balance
            </label>
            <select
              id="leave-balance-employee"
              value={balanceEmployeeEmail}
              onChange={handleBalanceEmployeeChange}
              className="w-full rounded-lg border border-white/30 bg-white/95 px-3 py-2 text-sm text-blue-950 shadow-sm outline-none transition focus:border-white focus:ring-2 focus:ring-white/50"
            >
              <option value="">Select an employee</option>
              {employeeOptions.map((employee) => {
                const email = String(employee.email_address || '').toLowerCase();
                const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || email;
                return <option key={email} value={email}>{name} ({email})</option>;
              })}
            </select>
          </div>
        </div>
      </motion.div>

      {balanceEmployeeEmail && (
        <Card className="overflow-hidden border border-indigo-100 bg-gradient-to-br from-sky-50 via-indigo-50 to-violet-50 shadow-sm">
          <Card.Header>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-indigo-950">Employee Leave Balance</h2>
                <p className="text-sm text-indigo-700/80">Review paid and unpaid leave totals, then make an auditable balance adjustment.</p>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {balanceLoading ? (
              <p className="text-sm text-indigo-700">Loading leave balance...</p>
            ) : balanceDetails ? (
              <div className="space-y-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <p className="font-medium text-indigo-950">{balanceDetails.employee?.name}</p>
                  <Button variant="primary" onClick={() => setShowAdjustmentModal(true)}>Adjust Paid Leave</Button>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {[
                    ['Available Paid', balanceDetails.balance?.available],
                    ['Paid Taken', balanceDetails.leaveSummary?.paidTaken],
                    ['Paid Pending', balanceDetails.leaveSummary?.paidPending],
                    ['Manual Adjustments', balanceDetails.balance?.adjustments],
                    ['Carry Forward', balanceDetails.balance?.carryForward],
                    ['Current Accrual', balanceDetails.balance?.accrued],
                    ['Unpaid Taken', balanceDetails.leaveSummary?.unpaidTaken],
                    ['Unpaid Pending', balanceDetails.leaveSummary?.unpaidPending],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-white/80 bg-white/75 p-3 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-indigo-500">{label}</p>
                      <p className="mt-1 text-xl font-semibold text-indigo-950">{Number(value || 0).toFixed(1)} days</p>
                    </div>
                  ))}
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-indigo-950">Adjustment History</h3>
                  {balanceDetails.adjustments?.length ? (
                    <div className="space-y-2">
                      {balanceDetails.adjustments.map((adjustment) => (
                        <div key={adjustment.id} className="flex flex-col gap-1 rounded-lg border border-indigo-100 bg-white/60 px-3 py-2 text-sm md:flex-row md:items-center md:justify-between">
                          <span className={adjustment.adjustmentType === 'CREDIT' ? 'font-medium text-green-700' : 'font-medium text-red-700'}>
                            {adjustment.adjustmentType === 'CREDIT' ? '+' : '-'}{Number(adjustment.days).toFixed(1)} days
                          </span>
                          <span className="text-indigo-800">{adjustment.reason}</span>
                          <span className="text-xs text-indigo-500">By {adjustment.createdBy}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-indigo-700">No manual adjustments for this employee.</p>}
                </div>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      )}

      {cancellationRequests.length > 0 && (
        <Card className="mb-6 border border-amber-200">
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Pending leave cancellations</h2>
            <p className="text-sm text-gray-600">Approve a cancellation to restore the employee's paid-leave balance.</p>
          </Card.Header>
          <Card.Body className="space-y-3">
            <ListPagination page={cancellationPage} totalPages={cancellationTotalPages} compact />
            <RefreshRegion loading={cancellationsLoading}>
            {cancellationRequests.map((request) => (
              <div key={request.id} className="rounded-lg border border-gray-200 p-4">
                <p className="font-medium text-gray-900">{request.employee?.name || request.employee?.email}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {request.leave_type || 'Leave'}: {formatDateDisplay(request.start_date)} to {formatDateDisplay(request.end_date)}
                </p>
                <p className="mt-2 text-sm text-gray-700">Cancellation reason: {request.reason}</p>
                <div className="mt-3 flex gap-3">
                  <Button variant="success" size="sm" onClick={() => decideCancellation(request, 'APPROVED')}>Approve cancellation</Button>
                  <Button variant="danger" size="sm" onClick={() => decideCancellation(request, 'REJECTED')}>Reject cancellation</Button>
                </div>
              </div>
            ))}
            <ListPagination
              page={cancellationPage} totalPages={cancellationTotalPages} total={cancellationTotal}
              label="leave cancellations" disabled={cancellationsLoading}
              onPageChange={(page) => {
                setCancellationPage(page);
                pageTopRef.current?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth', block: 'start' });
              }}
            />
            </RefreshRegion>
          </Card.Body>
        </Card>
      )}

      {/* Filter Tabs */}
      <Card>
        <Card.Body className="p-6">
          <LayoutGroup>
          <div role="group" aria-label="Filter leave requests by status" className="flex gap-2 overflow-x-auto p-1">
            {filterOptions.map((option) => (
              <motion.button
                type="button"
                key={option.value}
                aria-pressed={filter === option.value}
                onClick={() => {
                  if (filter === option.value) return;
                  scrollToPageRef.current = null;
                  setFilter(option.value);
                  setCurrentPage(1);
                }}
                className={`relative isolate flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                  filter === option.value ? 'border-transparent text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filter === option.value && (
                  <motion.span
                    layoutId="leave-approval-active-filter"
                    aria-hidden="true"
                    className="absolute inset-0 -z-10 rounded-lg bg-primary-600 shadow-sm"
                    transition={{ type: 'spring', stiffness: 450, damping: 38 }}
                  />
                )}
                <span>{option.label}</span>
                <span className={`min-w-[1.75rem] px-2 py-1 text-center text-xs rounded-full transition-colors duration-200 motion-reduce:transition-none ${
                  filter === option.value ? 'bg-white text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
          </LayoutGroup>
        </Card.Body>
      </Card>

      {/* Leave Requests */}
      <RefreshRegion loading={loading} dimWhileLoading={false}>
      <p className="mb-3 text-right text-sm text-gray-600" aria-label="Current leave request page">
        Page {currentPage} of {Math.max(1, totalPages)}
      </p>
      <motion.div
        key={displayedPage}
        initial={{ opacity: 0.65 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="space-y-4"
      >
        {safeLeaveRequests.length > 0 ? (
          safeLeaveRequests.map((request, index) => (
            <div
              key={request.id || index}
            >
              <Card initial={false} className="hover:shadow-lg transition-shadow duration-300">
                <Card.Body className="p-6">
                  {(() => {
                    const employeeEmail = request.email || request.employeeEmail || request.email_address || '';
                    const employee = employeeDirectory[String(employeeEmail).toLowerCase()];
                    const employeeName =
                      request.employeeName ||
                      (employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : '') ||
                      'Employee Name';
                    return (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <EmployeeAvatar
                        record={employee || request}
                        name={employeeName}
                        email={employeeEmail}
                        sizeClass="w-12 h-12"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {employeeName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full text-white ${
                            request.isPaid ? 'bg-green-600' : 'bg-red-600'
                          }`}>
                            {request.leaveType || (request.isPaid ? 'Paid Leave' : 'Unpaid Leave')}
                          </span>
                          {request.isFullDay === false && (
                            <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Half Day
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(request.status || 'pending')}`}>
                        {(request.status || 'pending').charAt(0).toUpperCase() + (request.status || 'pending').slice(1)}
                      </span>
                    </div>
                  </div>
                    );
                  })()}

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {request.scheduleReview && <p className="rounded bg-amber-50 p-3 text-sm text-amber-900 md:col-span-3">Schedule changed: proposed duration {request.scheduleReview.proposedDays} days. HR must review this in Settings → Company policies → Alternate Saturday Schedule before any additional deduction.</p>}
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Start Date</p>
                        <p className="font-medium">{request.startDate || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">End Date</p>
                        <p className="font-medium">{request.endDate || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-medium">{request.duration || '1'} day(s)</p>
                      </div>
                    </div>
                  </div>

                  {request.reason && (
                    <div className="mt-4 flex items-start space-x-2">
                      <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Reason</p>
                        <p className="text-gray-900">{request.reason}</p>
                      </div>
                    </div>
                  )}

                  {request.status === 'pending' && (
                    <div className="mt-6 flex space-x-3">
                      <Button
                        variant="success"
                        onClick={() => openDecisionModal(request, 'approved')}
                        className="flex items-center space-x-2"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Approve</span>
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => openDecisionModal(request, 'rejected')}
                        className="flex items-center space-x-2"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        <span>Reject</span>
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          ))
        ) : (
          <Card initial={false}>
            <Card.Body className="p-12 text-center">
              <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Leave Requests</h3>
              <p className="text-gray-600">No {filter !== 'all' ? filter : ''} leave requests found.</p>
            </Card.Body>
          </Card>
        )}
      </motion.div>
      <nav aria-label="Leave request pagination" className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm text-gray-600">
          Showing {leaveRequests.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–{leaveRequests.length ? (currentPage - 1) * PAGE_SIZE + leaveRequests.length : 0} of {totalRequests} requests.
        </p>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentPage((page) => page - 1)}
            disabled={loading || submittingDecision || currentPage <= 1}
            aria-label="Previous page of leave requests"
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              scrollToPageRef.current = `${filter}-${currentPage + 1}`;
              setCurrentPage((page) => page + 1);
            }}
            disabled={loading || submittingDecision || currentPage >= totalPages}
            aria-label="Next page of leave requests"
          >
            Next
          </Button>
        </div>
      </nav>
      </RefreshRegion>

      {/* Decision Modal */}
      {showDecisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {decisionAction === 'approved' ? 'Approve Leave Request' : 'Reject Leave Request'}
            </h3>
            <p className="text-gray-600 mb-4">
              {decisionAction === 'approved'
                ? `Add an optional approval note for ${selectedRequest?.employeeName}'s leave request:`
                : `Please provide a reason for rejecting ${selectedRequest?.employeeName}'s leave request:`}
            </p>
            <textarea
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value)}
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 ${decisionAction === 'approved' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-red-500 focus:border-red-500'}`}
              rows={4}
              placeholder={decisionAction === 'approved' ? 'Enter approval note (optional)...' : 'Enter rejection reason...'}
              required={decisionAction === 'rejected'}
            />
            <label className="mt-4 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Send this decision by email to the employee
            </label>
            <div className="flex space-x-3 mt-6">
              <Button
                variant={decisionAction === 'approved' ? 'success' : 'danger'}
                onClick={confirmDecision}
                disabled={submittingDecision || (decisionAction === 'rejected' && !decisionReason.trim())}
                className="flex-1"
              >
                {submittingDecision
                  ? 'Saving...'
                  : decisionAction === 'approved'
                  ? 'Confirm Approval'
                  : 'Confirm Rejection'}
              </Button>
              <Button
                variant="outline"
                onClick={closeDecisionModal}
                disabled={submittingDecision}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAdjustmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900">Adjust Paid Leave</h3>
            <p className="mt-1 text-sm text-gray-600">Changes are recorded separately and support half-day increments.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Adjustment</label>
                <select
                  value={adjustmentForm.adjustmentType}
                  onChange={(event) => setAdjustmentForm((current) => ({ ...current, adjustmentType: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="CREDIT">Increase available paid leave</option>
                  <option value="DEBIT">Decrease available paid leave</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Days</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={adjustmentForm.days}
                  onChange={(event) => setAdjustmentForm((current) => ({ ...current, days: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
                <textarea
                  rows={3}
                  value={adjustmentForm.reason}
                  onChange={(event) => setAdjustmentForm((current) => ({ ...current, reason: event.target.value }))}
                  placeholder="Explain this balance correction..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <Button variant="primary" onClick={submitAdjustment} disabled={submittingAdjustment} className="flex-1">
                {submittingAdjustment ? 'Saving...' : 'Save Adjustment'}
              </Button>
              <Button variant="outline" onClick={closeAdjustmentModal} disabled={submittingAdjustment} className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MotionConfig>
  );
};

export default LeaveApproval;
